import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, MessageSquare, Plus, Search, X } from "lucide-react";
import { useBloggers, useInbox, useMarkThreadRead, useSendMessage, type Blogger, type InboxThread } from "@/shared/api/wb";
import { Avatar, EmptyState, Eyebrow, PlatformIcon, Pill, type Platform } from "@/shared/ui";
import { cn } from "@/shared/lib/cn";

// ─── Shared content (used by both InboxPage and InboxModal) ──────────────────

export function InboxContent({ initialBloggerId, campaignId }: { initialBloggerId?: string; campaignId?: string }) {
  const { data } = useInbox();
  const { data: bloggers } = useBloggers({});
  const byId = new Map((bloggers ?? []).map((b) => [b.id, b]));
  const { search: locationSearch } = useLocation();
  const initialCamp = campaignId ?? new URLSearchParams(locationSearch).get("campaign") ?? "all";
  const [campFilter, setCampFilter] = useState<string>(initialCamp);
  const [activeIdx, setActiveIdx] = useState<number | null>(null);
  const navigate = useNavigate();
  const markRead = useMarkThreadRead();

  const threads = (data?.threads ?? []).filter((t) =>
    campFilter === "all" ? true : t.campaign_id === campFilter
  );
  const activeThread = activeIdx !== null ? threads[activeIdx] : null;

  useEffect(() => { setActiveIdx(null); }, [campFilter]);

  // Pre-select thread when initialBloggerId is provided (e.g. opened via "Write" button)
  useEffect(() => {
    if (!initialBloggerId || !data) return;
    const idx = (data.threads ?? []).findIndex((t) => t.blogger_id === initialBloggerId);
    if (idx >= 0) {
      setCampFilter("all");
      setActiveIdx(idx);
    }
  }, [initialBloggerId, data]);

  function openThread(idx: number) {
    setActiveIdx(idx);
    const t = threads[idx];
    if (t && t.unread > 0) markRead.mutate({ campaignId: t.campaign_id, bloggerId: t.blogger_id });
  }

  const allThreads = data?.threads ?? [];

  if (data && allThreads.length === 0) {
    return (
      <div className="h-full grid place-items-center">
        <EmptyState
          icon={<MessageSquare size={28} strokeWidth={1.8} />}
          title="Ещё нет диалогов"
          description={<>
            Чаты появятся, когда вы запустите РК — мы автоматически отправим приглашения
            блогерам, переписка с ними будет здесь.
          </>}
          primaryCta={{ label: "Запустить РК", to: "/campaign/new" }}
          secondaryLink={{ label: "Каталог блогеров", to: "/catalog" }}
        />
      </div>
    );
  }

  return (
    <div className="grid grid-cols-[320px_1fr] h-full overflow-hidden">
      <aside className="border-r border-ink-12 bg-[var(--bg-2)] flex flex-col overflow-hidden">
        <div className="px-4 py-3.5 border-b border-ink-12">
          {campFilter !== "all" && (
            <Link to={`/campaign/${campFilter}`} className="inline-flex items-center gap-1 text-[12px] text-ink-40 hover:text-ink mb-2">
              <ArrowLeft size={12} /> К размещениям
            </Link>
          )}
          <Eyebrow className="mb-2">Лента чатов</Eyebrow>
          <CampaignFilter
            value={campFilter}
            onChange={setCampFilter}
            campaigns={data?.campaigns ?? []}
            counts={countByCamp(data?.threads ?? [])}
          />
        </div>
        <div className="px-4 py-2.5">
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-white border border-ink-12 text-ink-40">
            <Search size={14} />
            <input placeholder="Поиск по чатам…" className="flex-1 bg-transparent outline-none text-[13px] text-ink placeholder:text-ink-40" />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {threads.map((t, idx) => {
            const b = byId.get(t.blogger_id);
            if (!b) return null;
            const isActive = activeIdx === idx;
            const time = (t.time.split(" · ").pop() ?? t.time);
            return (
              <button key={`${t.campaign_id}-${t.blogger_id}`} onClick={() => openThread(idx)}
                className={cn("w-full grid grid-cols-[40px_1fr_auto] gap-2.5 items-center px-4 py-2.5 text-left transition-colors border-b border-ink-12/40",
                  isActive ? "bg-white" : "hover:bg-ink-6")}>
                <Avatar name={b.full} size={40} grad={b.grad as [string, string]} />
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5 font-display font-semibold text-[13px] truncate">
                    <PlatformIcon platform={b.platform as Platform} size={11} /> {b.name}
                  </div>
                  <div className="text-[11px] text-ink-40 truncate font-display font-semibold mt-0.5">{t.campaign.product}</div>
                  <div className="text-[12px] text-ink-60 truncate mt-0.5">{t.last}</div>
                </div>
                <div className="text-right">
                  <div className="text-[10.5px] text-ink-40 font-mono">{time}</div>
                  {t.unread > 0 && (
                    <div className="mt-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-pill bg-lime text-[var(--fg-on-accent)] text-[10px] font-bold font-mono">{t.unread}</div>
                  )}
                </div>
              </button>
            );
          })}
          {threads.length === 0 && (
            <div className="text-center text-ink-40 text-[13px] py-10">Нет диалогов</div>
          )}
        </div>
      </aside>

      {activeThread && byId.get(activeThread.blogger_id) ? (
        <Thread
          thread={activeThread}
          blogger={byId.get(activeThread.blogger_id)!}
          quickReplies={data?.quick_replies ?? []}
          openCampaign={() => navigate(`/campaign/${activeThread.campaign_id}`)}
          onClose={() => setActiveIdx(null)}
        />
      ) : (
        <div className="grid place-items-center text-ink-40">Выберите диалог слева</div>
      )}
    </div>
  );
}

// ─── Page wrapper ─────────────────────────────────────────────────────────────

export function InboxPage() {
  return (
    <div className="h-[calc(100vh-60px)] overflow-hidden">
      <InboxContent />
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function countByCamp(threads: InboxThread[]): Record<string, number> {
  const out: Record<string, number> = { all: threads.length };
  for (const t of threads) out[t.campaign_id] = (out[t.campaign_id] ?? 0) + 1;
  return out;
}

function CampaignFilter({ value, onChange, campaigns, counts }: {
  value: string; onChange: (v: string) => void;
  campaigns: { id: string; product: string }[]; counts: Record<string, number>;
}) {
  return (
    <div className="flex gap-1.5 flex-wrap">
      <Pill variant={value === "all" ? "ink" : "outline"} onClick={() => onChange("all")}>
        Все РК <span className="font-mono opacity-60 ml-1">{counts.all ?? 0}</span>
      </Pill>
      {campaigns.map((c) => (
        <Pill key={c.id} variant={value === c.id ? "ink" : "outline"} onClick={() => onChange(c.id)}>
          {c.product} {counts[c.id] && <span className="font-mono opacity-60 ml-1">{counts[c.id]}</span>}
        </Pill>
      ))}
    </div>
  );
}

function Thread({ thread, blogger, quickReplies, openCampaign, onClose }: {
  thread: InboxThread; blogger: Blogger; quickReplies: string[]; openCampaign: () => void; onClose: () => void;
}) {
  const [text, setText] = useState("");
  const send = useSendMessage(thread.campaign_id);

  function onSend(content?: string) {
    const t = (content ?? text).trim();
    if (!t) return;
    send.mutate({ blogger_id: thread.blogger_id, text: t });
    setText("");
  }

  return (
    <div className="flex flex-col overflow-hidden h-full">
      <div className="px-5 py-3 border-b border-ink-12 flex items-center gap-3 bg-white flex-shrink-0">
        <Avatar name={blogger.full} size={32} grad={blogger.grad as [string, string]} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 font-display font-semibold text-[14px]">
            <PlatformIcon platform={blogger.platform as Platform} size={13} />
            {blogger.name}
            <span className="text-[12px] text-ink-40 font-normal">· {blogger.full}</span>
          </div>
          <button onClick={openCampaign} className="text-[12px] text-link hover:underline mt-0.5">
            РК «{thread.campaign.product}» →
          </button>
        </div>
        <button onClick={onClose} title="Закрыть диалог"
          className="w-8 h-8 grid place-items-center rounded-md text-ink-40 hover:bg-ink-6 hover:text-ink">
          <X size={16} />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-5 flex flex-col gap-1">
        {thread.messages.map((m, i) => (
          <div key={i} className={cn("flex flex-col gap-0.5 max-w-[60%]",
            m.from_who === "me" ? "self-end items-end" : "self-start items-start")}>
            <div className={cn("px-3.5 py-2 rounded-md text-[13.5px] leading-relaxed",
              m.from_who === "me" ? "bg-lime text-[var(--fg-on-accent)]" : "bg-white border border-ink-12 text-ink")}>{m.text}</div>
            <div className="text-[10.5px] text-ink-40">{m.time_label}</div>
          </div>
        ))}
      </div>

      <div className="border-t border-ink-12 px-4 py-3 bg-white flex-shrink-0">
        <div className="flex gap-1.5 mb-2 flex-wrap">
          {quickReplies.map((qr) => (
            <button key={qr} onClick={() => onSend(qr)}
              className="h-7 px-2.5 rounded-pill border border-ink-12 text-[12px] font-display font-semibold text-ink-60 hover:text-ink hover:border-ink/40">
              {qr}
            </button>
          ))}
        </div>
        <div className="flex gap-2 items-center">
          <button title="Прикрепить файл"
            className="w-9 h-9 grid place-items-center rounded-md text-ink-60 hover:bg-ink-6 hover:text-ink"><Plus size={18} /></button>
          <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && onSend()}
            placeholder="Сообщение…"
            className="flex-1 h-10 px-4 rounded-md border border-ink-12 bg-white text-[14px] outline-none focus:border-ink" />
          <button onClick={() => onSend()} disabled={!text.trim() || send.isPending}
            className="h-10 w-10 grid place-items-center rounded-md bg-lime text-[var(--fg-on-accent)] disabled:opacity-40 hover:[background:color-mix(in_srgb,var(--wb-lime)_88%,black)]"><ArrowRight size={16} strokeWidth={2.5} /></button>
        </div>
      </div>
    </div>
  );
}
